import { useEffect, useRef } from "react";

/**
 * Closes an open overlay (modal/dialog) when the user presses the browser
 * Back/Forward button (a `popstate`), so its backdrop can't get stuck on the
 * page that was navigated to.
 *
 * Why a raw `popstate` listener instead of a pushed history entry: the previous
 * approach (history.pushState on open + history.back() on close) raced with the
 * app's "close one modal, open another in the same tick" pattern — the closing
 * modal's history.back() popped the entry the next modal had just pushed,
 * slamming the new modal shut (opening New/Edit Application from the list never
 * worked). This hook touches history NOT AT ALL: opening, closing and swapping
 * modals never move the history stack, so those flows are untouched, while a
 * genuine Back/Forward press fires `popstate` and tears the overlay (and its
 * backdrop) down. Back therefore closes the popup AND moves the page back one
 * entry — the agreed fallback ("close the popup and then change the page").
 *
 * It deliberately avoids react-router's `useLocation` so the consuming
 * components stay renderable outside a Router (e.g. in unit tests).
 */
export function useCloseOnNavigate(isOpen: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    const onPopState = () => onCloseRef.current();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isOpen]);
}
